/**
 * NotificationDrawer — Slide-in panel for notifications.
 * Opens from right (LTR) or left (RTL) as an overlay.
 * Shows notifications grouped by: today, yesterday, older.
 * Includes swipe-to-close, Escape key, mark-all-read, time-ago formatting.
 */

import type { AweniContext, NotificationItem } from '../context/AweniContext';

let _drawerEl: HTMLElement | null = null;
let _backdropEl: HTMLElement | null = null;
let _listEl: HTMLElement | null = null;
let _ctx: AweniContext | null = null;
let _isOpen = false;
let _touchStartX = 0;

export function mountNotificationDrawer(container: HTMLElement, ctx: AweniContext): void {
  _ctx = ctx;
  const isRtl = ctx.i18n.dir === 'rtl';

  // Backdrop
  const backdrop = document.createElement('div');
  backdrop.style.cssText = 'position:fixed;inset:0;z-index:199;background:var(--backdrop);display:none;opacity:0;transition:opacity 200ms ease;';
  backdrop.addEventListener('click', () => closeNotificationDrawer());
  _backdropEl = backdrop;

  // Drawer panel
  const drawer = document.createElement('div');
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', ctx.i18n.t('notifications_title') || 'Notifications');
  drawer.style.cssText = `
    position: fixed; top: 0; bottom: 0; z-index: 200;
    width: min(360px, 85vw);
    background: var(--bg-primary);
    box-shadow: var(--shadow-lg);
    display: flex; flex-direction: column;
    transition: transform 250ms ease-out;
    ${isRtl ? 'left:0;transform:translateX(-100%);' : 'right:0;transform:translateX(100%);'}
  `;

  // Swipe-to-close
  drawer.addEventListener('touchstart', (e) => { _touchStartX = e.touches[0].clientX; }, { passive: true });
  drawer.addEventListener('touchend', (e) => {
    const diff = e.changedTouches[0].clientX - _touchStartX;
    if (isRtl && diff < -80) closeNotificationDrawer();
    if (!isRtl && diff > 80) closeNotificationDrawer();
  }, { passive: true });

  // Header
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:16px;border-bottom:1px solid var(--border);flex-shrink:0;';

  const title = document.createElement('h2');
  title.style.cssText = 'font-size:18px;font-weight:700;color:var(--text-primary);margin:0;';
  title.textContent = ctx.i18n.t('notifications_title') || 'Notifications';

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;align-items:center;';

  const markAllBtn = document.createElement('button');
  markAllBtn.style.cssText = 'font-size:12px;color:var(--accent);font-weight:600;padding:4px 8px;border-radius:4px;';
  markAllBtn.textContent = ctx.i18n.t('notifications_mark_all_read') || 'Mark all read';
  markAllBtn.addEventListener('click', async () => {
    await ctx.notifications.markAllRead();
    _renderList();
  });

  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('aria-label', ctx.i18n.t('common_close') || 'Close');
  closeBtn.style.cssText = 'width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:50%;';
  closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
  closeBtn.addEventListener('click', () => closeNotificationDrawer());

  actions.append(markAllBtn, closeBtn);
  header.append(title, actions);

  // Scrollable list
  const list = document.createElement('div');
  list.className = 'module-viewport';
  list.style.cssText = 'flex:1;overflow-y:auto;padding:8px 0;';
  _listEl = list;

  drawer.append(header, list);
  _drawerEl = drawer;

  container.appendChild(backdrop);
  container.appendChild(drawer);
}

export function openNotificationDrawer(): void {
  if (_isOpen || !_drawerEl || !_backdropEl || !_ctx) return;
  _isOpen = true;

  _backdropEl.style.display = 'block';
  requestAnimationFrame(() => {
    if (_backdropEl) _backdropEl.style.opacity = '1';
    if (_drawerEl) _drawerEl.style.transform = 'translateX(0)';
  });

  // Fetch latest and render
  _ctx.notifications.fetch().then(() => _renderList());

  document.addEventListener('keydown', _handleKeyboard);

  // Focus trap: focus the close button
  const close = _drawerEl?.querySelector('button[aria-label]') as HTMLElement | null;
  if (close) setTimeout(() => close.focus(), 250);
}

export function closeNotificationDrawer(): void {
  if (!_isOpen || !_drawerEl || !_backdropEl || !_ctx) return;
  _isOpen = false;

  const isRtl = _ctx.i18n.dir === 'rtl';
  _drawerEl.style.transform = isRtl ? 'translateX(-100%)' : 'translateX(100%)';
  _backdropEl.style.opacity = '0';
  setTimeout(() => { if (_backdropEl) _backdropEl.style.display = 'none'; }, 250);

  document.removeEventListener('keydown', _handleKeyboard);
}

/** Handle keyboard events: Escape to close, Tab to trap focus. */
function _handleKeyboard(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    closeNotificationDrawer();
    return;
  }
  // Focus trap within the drawer
  if (e.key === 'Tab' && _drawerEl) {
    const focusables = _drawerEl.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

// --- Rendering ---

function _renderList(): void {
  if (!_listEl || !_ctx) return;
  _listEl.innerHTML = '';

  const items = _ctx.notifications.items;
  if (items.length === 0) {
    const empty = document.createElement('div');
    empty.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 24px;';
    empty.innerHTML = `
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" stroke-width="1.5" style="margin-bottom:12px;opacity:0.5;">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
      <p style="font-size:14px;color:var(--text-secondary);">${_ctx.i18n.t('notifications_empty') || 'No notifications'}</p>
    `;
    _listEl.appendChild(empty);
    return;
  }

  // Group by date: today, yesterday, older
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400000);

  const groups: [string, NotificationItem[]][] = [
    ['Today', []],
    ['Yesterday', []],
    ['Older', []],
  ];

  for (const item of items) {
    const d = new Date(item.time);
    if (d >= todayStart) groups[0][1].push(item);
    else if (d >= yesterdayStart) groups[1][1].push(item);
    else groups[2][1].push(item);
  }

  for (const [label, groupItems] of groups) {
    if (groupItems.length === 0) continue;

    const groupHeader = document.createElement('div');
    groupHeader.style.cssText = 'padding:8px 16px;font-size:11px;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;';
    groupHeader.textContent = label;
    _listEl.appendChild(groupHeader);

    for (const item of groupItems) {
      _listEl.appendChild(_createNotificationItem(item));
    }
  }
}

function _createNotificationItem(item: NotificationItem): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = `
    padding: 12px 16px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    cursor: pointer;
    transition: background 150ms ease;
    ${!item.read ? 'background:var(--bg-secondary);' : ''}
  `;

  el.addEventListener('mouseenter', () => { el.style.background = 'var(--bg-secondary)'; });
  el.addEventListener('mouseleave', () => { el.style.background = item.read ? '' : 'var(--bg-secondary)'; });

  // Category icon circle
  const catColors: Record<string, string> = {
    achievement: 'rgba(245,158,11,0.15)',
    reminder: 'rgba(59,130,246,0.15)',
    social: 'rgba(139,92,246,0.15)',
    system: 'rgba(107,114,128,0.15)',
  };
  const catIcons: Record<string, string> = {
    achievement: '\u{1F3C6}',
    reminder: '\u{1F514}',
    social: '\u{1F465}',
    system: '\u{2139}\uFE0F',
  };

  const iconEl = document.createElement('div');
  iconEl.style.cssText = `
    width: 36px; height: 36px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; font-size: 16px;
    background: ${catColors[item.category] || catColors.system};
  `;
  iconEl.textContent = catIcons[item.category] || catIcons.system;

  // Content
  const content = document.createElement('div');
  content.style.cssText = 'flex:1;min-width:0;';

  const titleEl = document.createElement('p');
  titleEl.style.cssText = `font-size:13px;font-weight:${item.read ? '400' : '600'};color:var(--text-primary);margin:0;line-height:1.3;`;
  titleEl.textContent = item.title;

  const bodyEl = document.createElement('p');
  bodyEl.style.cssText = 'font-size:12px;color:var(--text-secondary);margin:2px 0 0;line-height:1.3;';
  bodyEl.textContent = item.body;

  const timeEl = document.createElement('span');
  timeEl.style.cssText = 'font-size:11px;color:var(--text-secondary);opacity:0.7;';
  timeEl.textContent = _timeAgo(item.time);

  content.append(titleEl, bodyEl, timeEl);
  el.append(iconEl, content);

  // Unread indicator dot
  if (!item.read) {
    const dot = document.createElement('span');
    dot.style.cssText = 'width:8px;height:8px;border-radius:50%;background:var(--accent);flex-shrink:0;margin-top:6px;';
    el.appendChild(dot);
  }

  // Click handler: mark read + navigate
  el.addEventListener('click', async () => {
    if (_ctx && !item.read) {
      await _ctx.notifications.markRead(item.id);
      item.read = true;
      el.style.background = '';
      titleEl.style.fontWeight = '400';
      // Remove dot
      const lastChild = el.lastElementChild as HTMLElement | null;
      if (lastChild && lastChild.style.borderRadius === '50%') lastChild.remove();
    }
    if (item.action_url && _ctx) {
      closeNotificationDrawer();
      _ctx.navigate(item.action_url);
    }
  });

  return el;
}

function _timeAgo(time: string): string {
  const diff = Date.now() - new Date(time).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(time).toLocaleDateString();
}
